/**
 * CsvImportModal — standalone modal for bulk CSV track/detection import.
 *
 * Four phases:
 *   1. File Drop Zone
 *   2. Column Mapping (auto-detect + manual override)
 *   3. Preview & Confirm
 *   4. Upload Progress & Results
 *
 * Opened from the Sensors list page — no specific sensor required.
 * Uses a synthetic sensor ID and pulls tenantId from auth store.
 */

import { Fragment, useState, useRef, useCallback, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { parseCsv } from '../../utils/csvParser';
import {
  TARGET_FIELDS,
  autoDetectMapping,
  applyMapping,
  validateMapping,
} from '../../utils/csvColumnMapper';
import { sendBatch } from '../../services/ingestionApi';

// ============================================================================
// TYPES
// ============================================================================

type Phase = 'drop' | 'mapping' | 'preview' | 'uploading';

interface CsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UploadResult {
  totalRows: number;
  imported: number;
  failed: number;
  skipped: number;
  errors: string[];
}

const BATCH_SIZE = 500;
const MAX_FILE_SIZE_MB = 50;

// ============================================================================
// COMPONENT
// ============================================================================

export default function CsvImportModal({ isOpen, onClose }: CsvImportModalProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Phase state
  const [phase, setPhase] = useState<Phase>('drop');
  const [isDragging, setIsDragging] = useState(false);

  // Parsed CSV state
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [sampleValues, setSampleValues] = useState<Record<string, string[]>>({});

  // Mapping state
  const [mapping, setMapping] = useState<Record<string, string>>({});

  // Upload state
  const [batchesDone, setBatchesDone] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Derived
  const mappedData = useMemo(() => {
    if (rawRows.length === 0 || Object.keys(mapping).length === 0) {
      return { mapped: [], skipped: 0 };
    }
    return applyMapping(rawRows, mapping);
  }, [rawRows, mapping]);

  const validation = useMemo(() => validateMapping(mapping), [mapping]);

  // ===========================================================================
  // RESET & CLOSE
  // ===========================================================================

  const resetState = useCallback(() => {
    setPhase('drop');
    setFileName('');
    setHeaders([]);
    setRawRows([]);
    setSampleValues({});
    setMapping({});
    setBatchesDone(0);
    setTotalBatches(0);
    setUploadResult(null);
    setIsUploading(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isUploading) return; // prevent closing during upload
    resetState();
    onClose();
  }, [isUploading, resetState, onClose]);

  // ===========================================================================
  // FILE HANDLING
  // ===========================================================================

  const handleFile = useCallback((file: File) => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['csv', 'tsv', 'txt'].includes(ext || '')) {
      toast.error('Please select a CSV, TSV, or TXT file.');
      return;
    }

    file.text().then((text) => {
      try {
        const { headers: hdrs, rows } = parseCsv(text);

        if (hdrs.length === 0 || rows.length === 0) {
          toast.error('The file appears to be empty or has no valid headers.');
          return;
        }

        // Build sample values (first 3 non-empty per column)
        const samples: Record<string, string[]> = {};
        for (const h of hdrs) {
          const vals: string[] = [];
          for (const row of rows) {
            if (vals.length >= 3) break;
            const v = row[h]?.trim();
            if (v) vals.push(v);
          }
          samples[h] = vals;
        }

        setFileName(file.name);
        setHeaders(hdrs);
        setRawRows(rows);
        setSampleValues(samples);

        const autoMap = autoDetectMapping(hdrs);
        const fullMapping: Record<string, string> = {};
        for (const h of hdrs) {
          fullMapping[h] = autoMap[h] || '';
        }
        setMapping(fullMapping);
        setPhase('mapping');
      } catch (err: any) {
        toast.error(`Failed to parse CSV: ${err.message || 'Unknown error'}`);
      }
    });
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const onFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = '';
    },
    [handleFile],
  );

  // ===========================================================================
  // MAPPING CHANGE
  // ===========================================================================

  const handleMappingChange = useCallback(
    (csvColumn: string, targetKey: string) => {
      setMapping((prev) => {
        const next = { ...prev };
        if (targetKey) {
          for (const key of Object.keys(next)) {
            if (next[key] === targetKey && key !== csvColumn) {
              next[key] = '';
            }
          }
        }
        next[csvColumn] = targetKey;
        return next;
      });
    },
    [],
  );

  // ===========================================================================
  // UPLOAD
  // ===========================================================================

  const startUpload = useCallback(async () => {
    const { mapped, skipped } = mappedData;
    if (mapped.length === 0) return;

    // Standalone context — no specific sensor
    const tenantId = useAuthStore.getState().user?.tenantId || 'default';
    const sensorId = 'csv-import';
    const sensorType = 'MULTI_SENSOR';

    const batches = Math.ceil(mapped.length / BATCH_SIZE);
    setTotalBatches(batches);
    setBatchesDone(0);
    setUploadResult(null);
    setIsUploading(true);
    setPhase('uploading');

    let imported = 0;
    let failed = 0;
    const errors: string[] = [];

    for (let i = 0; i < batches; i++) {
      const batch = mapped.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE);
      try {
        await sendBatch(batch, sensorId, tenantId, sensorType);
        imported += batch.length;
      } catch (err: any) {
        failed += batch.length;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          `Batch ${i + 1} failed`;
        errors.push(`Batch ${i + 1}: ${msg}`);
      }
      setBatchesDone(i + 1);
    }

    setUploadResult({
      totalRows: rawRows.length,
      imported,
      failed,
      skipped,
      errors,
    });
    setIsUploading(false);

    if (imported > 0) {
      toast.success(`Imported ${imported.toLocaleString()} tracks`);
    }

    queryClient.invalidateQueries({ queryKey: ['tracks'] });
    queryClient.invalidateQueries({ queryKey: ['sensors'] });
  }, [mappedData, rawRows.length, queryClient]);

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl rounded-xl bg-navy-800 border border-navy-700 p-6 shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-white">
                      Import Detections from CSV
                    </Dialog.Title>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Upload a CSV file with historical detection data. Columns will be auto-mapped to track fields.
                    </p>
                  </div>
                  {!isUploading && (
                    <button
                      onClick={handleClose}
                      className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-navy-700 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                {/* ── Phase 1: Drop Zone ── */}
                {phase === 'drop' && (
                  <div
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    className={`
                      relative flex flex-col items-center justify-center gap-3 p-10
                      border-2 border-dashed rounded-lg cursor-pointer transition-all
                      ${isDragging
                        ? 'border-accent bg-accent/5'
                        : 'border-navy-600 hover:border-navy-500 hover:bg-navy-800/30'
                      }
                    `}
                  >
                    <ArrowUpTrayIcon className={`w-10 h-10 ${isDragging ? 'text-accent' : 'text-gray-500'}`} />
                    <div className="text-center">
                      <p className="text-sm text-gray-300">
                        Drag &amp; drop a CSV file here, or{' '}
                        <span className="text-accent font-medium">browse files</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        CSV, TSV, or TXT up to {MAX_FILE_SIZE_MB}MB
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.tsv,.txt"
                      onChange={onFileSelect}
                      className="hidden"
                    />
                  </div>
                )}

                {/* ── Phase 2: Column Mapping ── */}
                {phase === 'mapping' && (
                  <div className="space-y-4">
                    {/* File info bar */}
                    <div className="flex items-center gap-4 px-4 py-2.5 bg-navy-900 rounded-lg border border-navy-700 text-xs">
                      <span className="text-gray-400">
                        File: <span className="text-white font-medium">{fileName}</span>
                      </span>
                      <span className="text-gray-600">|</span>
                      <span className="text-gray-400">
                        <span className="text-white font-medium">{rawRows.length.toLocaleString()}</span> rows
                      </span>
                      <span className="text-gray-600">|</span>
                      <span className="text-gray-400">
                        <span className="text-white font-medium">{headers.length}</span> columns
                      </span>
                    </div>

                    {/* Mapping table */}
                    <div className="border border-navy-700 rounded-lg overflow-hidden">
                      <div className="grid grid-cols-[1fr_auto_1fr] gap-0 text-xs font-medium text-gray-400 bg-navy-900/50 px-4 py-2 border-b border-navy-700">
                        <span>CSV Column</span>
                        <span className="w-8" />
                        <span>Map To</span>
                      </div>

                      <div className="divide-y divide-navy-800 max-h-[350px] overflow-y-auto">
                        {headers.map((header) => {
                          const targetKey = mapping[header] || '';
                          const isRequired = targetKey === 'latitude' || targetKey === 'longitude';
                          const isMapped = !!targetKey;

                          return (
                            <div
                              key={header}
                              className="grid grid-cols-[1fr_auto_1fr] gap-0 items-center px-4 py-2.5 hover:bg-navy-800/30 transition-colors"
                            >
                              <div className="min-w-0">
                                <span className="text-sm text-white font-mono truncate block">
                                  {header}
                                </span>
                                {sampleValues[header]?.length > 0 && (
                                  <span className="text-xs text-gray-600 truncate block mt-0.5">
                                    {sampleValues[header].join(', ')}
                                  </span>
                                )}
                              </div>

                              <div className="w-8 flex justify-center">
                                {isMapped ? (
                                  <div className={`w-2 h-2 rounded-full ${isRequired ? 'bg-accent' : 'bg-sky'}`} />
                                ) : (
                                  <span className="text-gray-700">&rarr;</span>
                                )}
                              </div>

                              <div>
                                <select
                                  value={targetKey}
                                  onChange={(e) => handleMappingChange(header, e.target.value)}
                                  className={`
                                    w-full text-sm rounded-md border px-2.5 py-1.5
                                    bg-navy-900 focus:outline-none focus:ring-1 focus:ring-accent
                                    ${isMapped ? 'border-accent/30 text-white' : 'border-navy-700 text-gray-500'}
                                  `}
                                >
                                  <option value="">— Skip —</option>
                                  {TARGET_FIELDS.map((field) => (
                                    <option key={field.key} value={field.key}>
                                      {field.label}
                                      {field.required ? ' *' : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Validation warnings */}
                    {!validation.valid && (
                      <div className="flex items-start gap-2 px-3 py-2 bg-alert/10 border border-alert/20 rounded-lg text-xs text-alert">
                        <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <div>
                          {validation.errors.map((err, i) => (
                            <p key={i}>{err}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-navy-700/50">
                      <button onClick={handleClose} className="btn-secondary text-sm">
                        Cancel
                      </button>
                      <button
                        onClick={() => setPhase('preview')}
                        disabled={!validation.valid}
                        className="btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Preview &amp; Import
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Phase 3: Preview & Confirm ── */}
                {phase === 'preview' && (
                  <div className="space-y-4">
                    {/* Stats bar */}
                    <div className="flex items-center gap-4 px-4 py-2.5 bg-navy-900 rounded-lg border border-navy-700 text-xs">
                      <span className="text-gray-400">
                        <span className="text-accent font-medium">
                          {mappedData.mapped.length.toLocaleString()}
                        </span>{' '}
                        tracks ready
                      </span>
                      {mappedData.skipped > 0 && (
                        <>
                          <span className="text-gray-600">|</span>
                          <span className="text-yellow-500">
                            {mappedData.skipped.toLocaleString()} rows skipped
                          </span>
                        </>
                      )}
                      <span className="text-gray-600">|</span>
                      <span className="text-gray-400">
                        {Math.ceil(mappedData.mapped.length / BATCH_SIZE)} batch
                        {Math.ceil(mappedData.mapped.length / BATCH_SIZE) !== 1 ? 'es' : ''}
                      </span>
                    </div>

                    {/* Preview table */}
                    <div className="border border-navy-700 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-navy-900/50 border-b border-navy-700">
                              {Object.values(mapping)
                                .filter(Boolean)
                                .map((targetKey) => {
                                  const field = TARGET_FIELDS.find((f) => f.key === targetKey);
                                  return (
                                    <th
                                      key={targetKey}
                                      className="px-3 py-2 text-left font-medium text-gray-400 whitespace-nowrap"
                                    >
                                      {field?.label || targetKey}
                                    </th>
                                  );
                                })}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-navy-800">
                            {mappedData.mapped.slice(0, 10).map((row, i) => (
                              <tr key={i} className="hover:bg-navy-800/30 transition-colors">
                                {Object.values(mapping)
                                  .filter(Boolean)
                                  .map((targetKey) => (
                                    <td
                                      key={targetKey}
                                      className="px-3 py-2 text-gray-300 whitespace-nowrap font-mono"
                                    >
                                      {row[targetKey] != null ? String(row[targetKey]) : '—'}
                                    </td>
                                  ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {mappedData.mapped.length > 10 && (
                        <div className="px-3 py-2 text-xs text-gray-600 border-t border-navy-800 bg-navy-900/30">
                          Showing first 10 of {mappedData.mapped.length.toLocaleString()} rows
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-navy-700/50">
                      <button
                        onClick={() => setPhase('mapping')}
                        className="btn-secondary text-sm"
                      >
                        Back
                      </button>
                      <button onClick={startUpload} className="btn-primary text-sm">
                        Import {mappedData.mapped.length.toLocaleString()} Tracks
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Phase 4: Upload Progress & Results ── */}
                {phase === 'uploading' && (
                  <div className="space-y-4">
                    {/* Progress bar */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">
                          {isUploading
                            ? `Uploading batch ${batchesDone} of ${totalBatches}…`
                            : 'Upload complete'}
                        </span>
                        <span className="text-white font-medium">
                          {totalBatches > 0
                            ? `${Math.round((batchesDone / totalBatches) * 100)}%`
                            : '0%'}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-navy-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-accent rounded-full transition-all duration-300"
                          style={{
                            width: `${totalBatches > 0 ? (batchesDone / totalBatches) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>

                    {/* Result summary */}
                    {uploadResult && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                          <div className="p-3 bg-navy-900 rounded-lg border border-navy-700 text-center">
                            <CheckCircleIcon className="w-5 h-5 text-accent mx-auto mb-1" />
                            <p className="text-lg font-semibold text-accent">
                              {uploadResult.imported.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500">Imported</p>
                          </div>
                          <div className="p-3 bg-navy-900 rounded-lg border border-navy-700 text-center">
                            <XCircleIcon className="w-5 h-5 text-alert mx-auto mb-1" />
                            <p className="text-lg font-semibold text-alert">
                              {uploadResult.failed.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500">Failed</p>
                          </div>
                          <div className="p-3 bg-navy-900 rounded-lg border border-navy-700 text-center">
                            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                            <p className="text-lg font-semibold text-yellow-500">
                              {uploadResult.skipped.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500">Skipped</p>
                          </div>
                        </div>

                        {uploadResult.errors.length > 0 && (
                          <div className="px-3 py-2 bg-alert/10 border border-alert/20 rounded-lg text-xs text-alert space-y-1 max-h-32 overflow-y-auto">
                            {uploadResult.errors.map((err, i) => (
                              <p key={i}>{err}</p>
                            ))}
                          </div>
                        )}

                        <div className="flex justify-end pt-2 border-t border-navy-700/50">
                          <button onClick={handleClose} className="btn-primary text-sm">
                            Done
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
